import { PRICING_POC_FACETS_QUERY_KEY } from "@entities/pricingWorkload/lib/constants";
import AddCommercials from "@entities/PricingProject/ui/AddCommercials";
import { CommercialsActionsMenu } from "@entities/PricingProject/ui/CommercialsActionsMenu";
import { NegotiationCostRange } from "@entities/PricingProject/ui/NegotiationCostRange";
import BrandPaymentTerms from "@features/BrandPaymentTerms";
import { BriefTimelinePopOver } from "@features/briefTimeline/BriefTimelinePopOver";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { InrAmountWordsLine, Tooltip } from "@shared/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Info } from "lucide-react";
import styles from "./styles.module.scss";

type PricingCreatorCommercialsCellProps = {
  record: IBriefInfluencerV2;
  briefId?: string;
  userId?: string;
};

function PendingStatus({ label }: { label: string }) {
  return (
    <Tooltip title={label}>
      <span className={styles.pendingStatus}>
        <Info size={12} />
        <span className={styles.pendingLabel}>{label}</span>
      </span>
    </Tooltip>
  );
}

export function PricingCreatorCommercialsCell({
  record,
  briefId,
  userId,
}: PricingCreatorCommercialsCellProps) {
  const queryClient = useQueryClient();

  if (record.isCreatorOnboardPending) {
    return <PendingStatus label="Onboarding Pending" />;
  }
  if (record.isDeliverablesPending) {
    return <PendingStatus label="Deliverables Pending" />;
  }
  if (!record.isPrimaryContactAdded) {
    return <PendingStatus label="Primary contact not added" />;
  }

  if (record.isCreatorDeclined || record.isBrandDeclined) {
    return (
      <div className={styles.costRow}>
        <BriefTimelinePopOver briefInfluencer={record} type="influencer" />
        <p className={styles.costAmount}>
          {record.influencerCost?.cost
            ? `₹${record.influencerCost.cost}`
            : "NA"}
        </p>
      </div>
    );
  }

  if (!record.influencerCost?.cost) {
    return (
      <AddCommercials
        key={record._id}
        briefId={briefId || ""}
        briefInfluencer={record}
        type="create"
        pocId={userId || ""}
      />
    );
  }

  const isNegotiation =
    Boolean(record.targetInfluencerCost) &&
    record.offerStatus === "negotiations-requested";

  return (
    <div className={styles.cell}>
      <div className={styles.costRow}>
        <BriefTimelinePopOver briefInfluencer={record} type="influencer" />
        {isNegotiation && record.targetInfluencerCost ? (
          <NegotiationCostRange
            fromCost={record.influencerCost.cost}
            toCost={record.targetInfluencerCost.cost}
          />
        ) : (
          <>
            <p className={styles.costAmount}>₹{record.influencerCost.cost}</p>
            {!record.isQueuedForPurchaseLot ? (
              <CommercialsActionsMenu
                briefId={briefId || ""}
                briefInfluencer={record}
                pocId={userId || ""}
                showAddNew
              />
            ) : null}
          </>
        )}
      </div>
      {!isNegotiation ? (
        <InrAmountWordsLine
          value={record.influencerCost.cost}
          className={styles.amountInWords}
        />
      ) : null}
      <span className={styles.paymentTermsText}>
        Payment terms:{" "}
        <BrandPaymentTerms
          briefId={briefId || ""}
          briefInfluencer={record}
          updateBriefInfluencer={() => {
            queryClient.invalidateQueries({
              queryKey: PRICING_POC_FACETS_QUERY_KEY(
                briefId || "",
                userId || ""
              ),
            });
          }}
        />
      </span>
      {isNegotiation ? (
        <AddCommercials
          key={record._id}
          briefId={briefId || ""}
          briefInfluencer={record}
          type="create-new"
          pocId={userId || ""}
        />
      ) : null}
    </div>
  );
}
